import type { ReactElement } from "react";
import { CheckStyle, IconAlignment, Size } from "../UiEnums";
import type { Item } from "../items";
import "./Checkbox.scss";
import "@/shared/style/design_system/Fonts.scss";
import classNames from "classnames";
import { isEmpty } from "@/shared/utils/Strings.ts";

interface Properties {
  alignIcon?: IconAlignment;
  onChange: (item: Item, selected: boolean) => void;
  size?: Size;
  checkStyle?: CheckStyle;
  selected?: boolean;
  disabled?: boolean;
  item: Item;
}

export default function Checkbox({
  alignIcon = IconAlignment.LEFT,
  onChange,
  size = Size.LARGE,
  checkStyle = CheckStyle.SQUARE,
  selected = false,
  disabled = false,
  item,
}: Properties): ReactElement {
  function onClick(): void {
    onChange(item, !selected);
  }

  function limitSize(): Size {
    // Cap generic checkbox size to what's defined in design system:
    const capSize = (): Size => {
      switch (size) {
        case Size.XXXLARGE:
        case Size.XXLARGE:
        case Size.XLARGE:
          return Size.LARGE;
        case Size.MEDIUM:
          return Size.SMALL;
        default:
          return size;
      }
    };
    const cappedSize = capSize();
    if (cappedSize !== size) {
      console.warn(
        `Checkbox isn't available for size ${size}, using ${cappedSize} instead`,
      );
    }
    return cappedSize;
  }

  function checkboxClasses(): string {
    return classNames(
      "checkbox",
      sizeClass(),
      !isEmpty(item.label) ? "with-label" : "",
      disabled && "disabled",
      selected && "selected",
    );
  }

  function sizeClass(): string {
    return Size[limitSize()].toLocaleLowerCase();
  }

  function checkClasses(): string {
    return classNames(
      "check-container",
      checkStyle === CheckStyle.DISK && "disk",
      selected && "selected",
    );
  }

  const iconDiv = (
    <div className={checkClasses()}>
      <div className="icon"></div>
    </div>
  );

  return (
    <div
      className={checkboxClasses()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {alignIcon === IconAlignment.LEFT && iconDiv}
      <div className="checkbox-label" title={item.label}>
        {item.label}
      </div>
      {alignIcon === IconAlignment.RIGHT && iconDiv}
    </div>
  );
}
